/**
 * One piece of evidence (layer 3): a row that expands to the kind's full evidence.
 * A browser test is titled by the test's name with its description (what it is for)
 * always visible under it; other kinds are titled by what they asserted.
 *
 * @module features/proofs/components/EvidenceItem
 */
import React from 'react';
import { Badge, CollapsibleSection, Text } from '@chatsdet/ui';
import type { ProofEvidence } from '../types';
import { EvalEvidenceBody, evalRowSummary } from './EvalEvidenceBody';
import { E2eEvidenceBody } from './E2eEvidenceBody';
import { ManualBadge } from './ManualBadge';
import { ManualEvidenceBody, manualFirstLine } from './ManualEvidenceBody';
import { ItemStatusBadge } from './ItemStatusBadge';
import { PerfEvidenceBody, perfRowSummary } from './PerfEvidenceBody';

export const EvidenceItem: React.FC<{ evidence: ProofEvidence; defaultExpanded: boolean }> = ({ evidence, defaultExpanded }) => {
  const label =
    evidence.kind === 'manual' ? manualFirstLine(evidence) : evidence.kind === 'e2e' ? (evidence.testName ?? evidence.asserted) : evidence.asserted;
  const spirit = evidence.kind === 'e2e' ? evidence.testDescription : null;
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const body = (() => {
    switch (evidence.kind) {
      case 'e2e':
        return <E2eEvidenceBody item={evidence} expanded={expanded} />;
      case 'manual':
        return <ManualEvidenceBody item={evidence} />;
      case 'perf':
        return <PerfEvidenceBody item={evidence} />;
      case 'eval':
        return <EvalEvidenceBody item={evidence} />;
    }
  })();
  return (
    <CollapsibleSection
      hideBorder
      label={label}
      header={
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Text weight={evidence.kind === 'e2e' ? 'semibold' : undefined}>{label}</Text>
            {evidence.kind === 'manual' ? (
              <ManualBadge status={evidence.status} testId="manual-item-badge" />
            ) : (
              <ItemStatusBadge status={evidence.status} />
            )}
            {evidence.kind === 'e2e' && evidence.existing && (
              <Badge
                variant="secondary"
                size="sm"
                data-testid="existing-test-badge"
                title="An existing test that already ran on its own. It shows this still works; it is not new coverage."
              >
                Existing test
              </Badge>
            )}
            {evidence.kind === 'perf' && <Text color="muted" size="sm">{perfRowSummary(evidence)}</Text>}
            {evidence.kind === 'eval' && evidence.result && <Text color="muted" size="sm">{evalRowSummary(evidence.result)}</Text>}
          </div>
          {spirit && (
            <Text color="muted" size="sm" data-testid="evidence-spirit">
              {spirit}
            </Text>
          )}
        </div>
      }
      isExpanded={expanded}
      onExpandedChange={setExpanded}
      bodyClassName="space-y-4 pl-6"
    >
      {body}
    </CollapsibleSection>
  );
};
